/**
 * LMSLearnerDashboardPage.jsx
 * ============================
 * Sprint LMS-X — Premium Learner Command Center.
 *
 * Reimagined as a "Global Delegate Preparation Academy" cockpit:
 *  - Cinematic hero with name, tier, overall readiness score
 *  - 6-skill radar (mun/research/speaking/writing/diplomacy/leadership)
 *  - My Learning Journey (active + completed courses)
 *  - Premium Prep Tools grid (Diploks-powered)
 *  - Upcoming Tasks ribbon
 *  - Achievements (badges + podium points + streak)
 *
 * Design language:
 *  - Deep navy + gold accents
 *  - Glass cards, gradient hero
 *  - Mobile-first responsive grid
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Trophy, Flame, Globe2, BookOpen, Mic2, ScrollText, ShieldHalf,
  GraduationCap, Sparkles, PlayCircle, Coins, Target, Zap,
  ArrowRight, Award, Crown, Compass,
} from "lucide-react";

const PREMIUM_TOOL_META = {
  lms_position_paper_review:  { icon: ScrollText,   tone: "amber",   route: "/dashboard/lms/mun-prep?tab=paper" },
  lms_speech_feedback:        { icon: Mic2,          tone: "rose",    route: "/dashboard/lms/mun-prep?tab=speech" },
  lms_country_research_brief: { icon: Globe2,        tone: "blue",    route: "/dashboard/lms/mun-prep?tab=country" },
  lms_resolution_review:      { icon: ShieldHalf,    tone: "violet",  route: "/dashboard/lms/mun-prep?tab=resolution" },
  lms_readiness_report:       { icon: Crown,         tone: "emerald", route: "/dashboard/lms/mun-prep?tab=report" },
  lms_debate_argument_builder:{ icon: Zap,           tone: "amber",   route: "/dashboard/lms/mun-prep?tab=debate" },
  lms_crisis_simulation:      { icon: Compass,       tone: "rose",    route: "/dashboard/lms/mun-prep?tab=crisis" },
  lms_parent_report:          { icon: GraduationCap, tone: "blue",    route: "/dashboard/lms" },
};

const TONE_BG = {
  amber:   "from-amber-50 to-yellow-50 border-amber-200",
  rose:    "from-rose-50 to-pink-50 border-rose-200",
  blue:    "from-sky-50 to-blue-50 border-sky-200",
  violet:  "from-violet-50 to-purple-50 border-violet-200",
  emerald: "from-emerald-50 to-teal-50 border-emerald-200",
};

const SKILL_META = [
  { key: "mun_readiness", label: "MUN", icon: Trophy },
  { key: "research_readiness", label: "Research", icon: BookOpen },
  { key: "speaking_readiness", label: "Speaking", icon: Mic2 },
  { key: "writing_readiness", label: "Writing", icon: ScrollText },
  { key: "diplomacy_readiness", label: "Diplomacy", icon: Globe2 },
  { key: "leadership_readiness", label: "Leadership", icon: Crown },
];

export default function LMSLearnerDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lms/dashboard");
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return (
      <DashboardLayout title="Learning Academy">
        <div className="grid h-72 place-items-center text-sm text-slate-500">
          Loading your Diplomexa Learning Academy…
        </div>
      </DashboardLayout>
    );
  }

  const { readiness, active_courses, completed_courses, achievements,
           upcoming_tasks, diploks_balance, premium_tools, user } = data;

  return (
    <DashboardLayout title="Diplomexa Global Learning Academy">
      <div data-testid="lmsx-learner-dashboard" className="space-y-6 pb-12">
        {/* Hero command center */}
        <section
          data-testid="lmsx-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-8 shadow-2xl"
        >
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl"></div>
          <div className="absolute -bottom-32 -left-12 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl"></div>
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-white">
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
                <GraduationCap size={12} />
                Global Delegate Preparation Academy
              </p>
              <h1 className="mt-2 text-3xl md:text-4xl font-bold leading-tight">
                Welcome, {user.name || "Delegate"}
              </h1>
              <p className="mt-2 max-w-xl text-sm text-blue-100">
                Your readiness, courses, MUN prep tools and certificates —
                all in one cockpit.  Powered by Diplomexa Intelligence and
                the Diploks Engine.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/dashboard/lms/mun-prep"
                  data-testid="lmsx-hero-mun-prep"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30] shadow-lg"
                >
                  <Sparkles size={12} /> Open MUN Prep Hub
                </Link>
                <Link
                  to="/dashboard/diploks/personal/topup"
                  data-testid="lmsx-hero-topup"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
                >
                  <Coins size={12} /> Top up Diploks
                </Link>
              </div>
            </div>

            {/* Readiness gauge */}
            <div className="grid place-items-center">
              <ReadinessGauge score={readiness.overall_score} tier={readiness.tier} />
              <p data-testid="lmsx-readiness-tier" className="mt-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
                {readiness.tier}
              </p>
            </div>
          </div>

          {/* Compact stat strip */}
          <div className="relative mt-6 grid grid-cols-2 gap-2 md:grid-cols-4">
            <Stat icon={Flame} label="Streak" value={`${achievements.learning_streak_days || 0} d`} testid="lmsx-stat-streak" />
            <Stat icon={Award} label="Badges" value={achievements.badges?.length || 0} testid="lmsx-stat-badges" />
            <Stat icon={Trophy} label="Podium Points" value={achievements.podium_points || 0} testid="lmsx-stat-podium" />
            <Stat icon={Coins} label="Diploks" value={diploks_balance?.total_available ?? 0} testid="lmsx-stat-diploks" />
          </div>
        </section>

        {/* Readiness skill radar */}
        <section
          data-testid="lmsx-readiness-panel"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#0F4C81] font-bold flex items-center gap-1">
                <Target size={12} /> Conference Readiness Score
              </p>
              <h2 className="mt-1 text-lg font-semibold text-[#0A192F]">
                6-skill diplomacy index
              </h2>
            </div>
            <button
              data-testid="lmsx-readiness-report-btn"
              onClick={() => navigate("/dashboard/lms/mun-prep?tab=report")}
              className="inline-flex items-center gap-1 rounded-lg border border-[#0F4C81]/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#0F4C81] hover:bg-blue-50"
            >
              Get full report <ArrowRight size={12} />
            </button>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {SKILL_META.map((s) => {
              const v = readiness.skills?.[s.key] ?? 0;
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`lmsx-skill-${s.key}`}
                  className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
                >
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-600">
                    <Icon size={12} />
                    {s.label}
                  </div>
                  <div className="mt-2 text-2xl font-bold text-[#0A192F]">{v}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                      style={{ width: `${v}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Premium tools grid */}
        <section data-testid="lmsx-premium-tools" className="space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1">
                <Sparkles size={12} /> Premium · Uses Diploks
              </p>
              <h2 className="mt-1 text-lg font-semibold text-[#0A192F]">
                Diplomexa Premium Prep Tools
              </h2>
            </div>
            <span className="text-[11px] text-slate-500">
              Basic LMS access remains free · Premium tools earn you faster
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            {(premium_tools || []).map((t) => {
              const meta = PREMIUM_TOOL_META[t.key] || { icon: Sparkles, tone: "blue", route: "/dashboard/lms/mun-prep" };
              const Icon = meta.icon;
              return (
                <Link
                  key={t.key}
                  to={meta.route}
                  data-testid={`lmsx-tool-${t.key}`}
                  className={`group rounded-2xl border bg-gradient-to-br p-5 transition hover:shadow-lg hover:-translate-y-0.5 ${TONE_BG[meta.tone] || TONE_BG.blue}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
                      <Icon size={16} className="text-[#0A192F]" />
                    </div>
                    <span className="rounded-full bg-[#0A192F] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]">
                      {t.cost ?? 0} Diploks
                    </span>
                  </div>
                  <h3 className="mt-3 text-sm font-bold text-[#0A192F]">
                    {t.label}
                  </h3>
                  <p className="mt-1 text-[11px] text-slate-600">
                    Uses Diplomexa Intelligence to produce a high-quality output you can save, share or export.
                  </p>
                  <div className="mt-3 inline-flex items-center text-[11px] font-semibold uppercase tracking-wider text-[#0F4C81] group-hover:text-[#D4AF37]">
                    Open tool <ArrowRight size={12} className="ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Learning journey + upcoming */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Active courses */}
          <section
            data-testid="lmsx-active-courses"
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <PlayCircle size={14} className="text-[#D4AF37]" /> My Learning Journey
              </h2>
              <Link to="/dashboard/courses" className="text-xs text-[#0F4C81] hover:underline">
                Discover more →
              </Link>
            </div>
            <div className="mt-3 space-y-3">
              {(active_courses || []).length === 0 ? (
                <EmptyCourses />
              ) : (
                (active_courses || []).map((c) => (
                  <div
                    key={c.course_id}
                    data-testid={`lmsx-course-row-${c.course_id}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#D4AF37]/60"
                  >
                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37]">
                      <BookOpen size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-[#0A192F]">{c.course_title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="capitalize">{c.course_level}</span>
                        <span>·</span>
                        <span>{c.lessons_completed}/{c.lessons_total} lessons</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                              style={{ width: `${c.progress_pct}%` }}></div>
                      </div>
                    </div>
                    <Link
                      to={`/dashboard/courses/${c.course_id}`}
                      className="inline-flex items-center rounded-md border border-[#0F4C81]/40 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white"
                    >
                      Resume
                    </Link>
                  </div>
                ))
              )}
            </div>
            {(completed_courses || []).length > 0 ? (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-[12px] text-emerald-800">
                <span className="font-bold">{completed_courses.length}</span> course
                {completed_courses.length === 1 ? "" : "s"} completed — your portfolio is growing.
              </div>
            ) : null}
          </section>

          {/* Upcoming tasks */}
          <section
            data-testid="lmsx-upcoming"
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
              <Compass size={14} className="text-[#D4AF37]" /> Recommended next
            </h2>
            <div className="mt-3 space-y-2">
              {(upcoming_tasks || []).length === 0 ? (
                <p className="text-xs text-slate-500">You&apos;re all caught up — generate a Readiness Report.</p>
              ) : (
                upcoming_tasks.map((t, i) => (
                  <div
                    key={i}
                    data-testid={`lmsx-upcoming-${i}`}
                    className="rounded-lg border border-slate-200 px-3 py-2 hover:border-[#D4AF37]/60"
                  >
                    <div className="text-[11px] uppercase tracking-wider text-[#0F4C81] font-bold">
                      {t.type?.replaceAll("_", " ") || "Task"}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-700">{t.title}</div>
                    {typeof t.progress_pct === "number" ? (
                      <div className="mt-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full rounded-full bg-[#0F4C81]"
                              style={{ width: `${t.progress_pct}%` }}></div>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        {/* Achievements */}
        <section
          data-testid="lmsx-achievements"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
            <Award size={14} className="text-[#D4AF37]" /> Achievements
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center">
              <Trophy size={20} className="mx-auto text-amber-600" />
              <div className="mt-1 text-2xl font-bold text-amber-900">
                {achievements.podium_points || 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-amber-700">Podium Points</div>
              <div className="mt-1 text-[10px] text-amber-700">Separate from Diploks</div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-center">
              <Flame size={20} className="mx-auto text-rose-600" />
              <div className="mt-1 text-2xl font-bold text-rose-900">
                {achievements.learning_streak_days || 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-rose-700">Day Streak</div>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center">
              <Award size={20} className="mx-auto text-blue-600" />
              <div className="mt-1 text-2xl font-bold text-blue-900">
                {achievements.badges?.length || 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Badges</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
              <Sparkles size={20} className="mx-auto text-emerald-600" />
              <div className="mt-1 text-2xl font-bold text-emerald-900">
                {achievements.premium_actions_used_30d || 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Premium runs (30d)</div>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function ReadinessGauge({ score, tier }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const circumference = 2 * Math.PI * 60;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div data-testid="lmsx-readiness-gauge" className="relative grid h-44 w-44 place-items-center">
      <svg className="absolute h-full w-full -rotate-90" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r="60" stroke="rgba(255,255,255,0.15)" strokeWidth="10" fill="none" />
        <circle
          cx="70" cy="70" r="60"
          stroke="#D4AF37" strokeWidth="10" fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 800ms ease" }}
        />
      </svg>
      <div className="relative text-center">
        <div className="text-[10px] uppercase tracking-[0.28em] text-blue-100">Readiness</div>
        <div data-testid="lmsx-readiness-score" className="text-5xl font-bold text-white leading-none">{pct}</div>
        <div className="text-[10px] text-blue-100">/ 100</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-blue-100">
        <Icon size={12} />
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

function EmptyCourses() {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
      <BookOpen size={28} className="mx-auto text-slate-400" />
      <p className="mt-2 text-sm font-semibold text-slate-700">Start your learning journey</p>
      <p className="mt-1 text-xs text-slate-500">Discover MUN prep, leadership and global affairs courses tailored to your role.</p>
      <Link
        to="/dashboard/courses"
        data-testid="lmsx-empty-discover"
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F4C81]"
      >
        Discover courses <ArrowRight size={12} />
      </Link>
    </div>
  );
}
